import type { GameSummary } from "@pax/api-contract/games/v1alpha1";

/**
 * A pile is a way your games already fall together: a scenario you keep
 * coming back to, a map entity you keep playing as. Nobody files anything — a
 * pile exists because two games have something in common, and stops existing
 * when they no longer do.
 */
export interface Pile {
  id: string;
  name: string;
  games: GameSummary[];
}

/**
 * The piles in a set of games, in the order the shelf offers them.
 *
 * Piles are read off whatever the shelf is currently showing rather than off
 * everything you own, so searching leaves only the piles with something in
 * them and their counts say how much of each the search found.
 */
export function pilesOf(games: GameSummary[]): Pile[] {
  const piles: Pile[] = [];

  const gather = (id: string, name: string, belongs: (game: GameSummary) => boolean) => {
    const within = games.filter(belongs);
    // Two games make a pile. All of them do not: a pile holding the whole
    // shelf is the shelf with a name on it, and picking it would open the
    // page you are already reading.
    if (within.length < 2 || within.length === games.length) return;
    // A pile that is another pile in different words is not a second pile.
    const gathered = piles.some(
      pile =>
        pile.games.length === within.length && pile.games.every((game, at) => game === within[at]),
    );
    if (!gathered) piles.push({ id, name, games: within });
  };

  for (const game of uniqueBy(games, game => game.preset.id)) {
    gather(`of:${game.preset.id}`, game.preset.title, other => other.preset.id === game.preset.id);
  }
  for (const game of uniqueBy(games, game => game.membership.perspectiveName)) {
    const polity = game.membership.perspectiveName;
    if (!polity) continue;
    gather(`as:${polity}`, `As ${polity}`, other => other.membership.perspectiveName === polity);
  }

  return piles;
}

/** How long a pile id can be, which is a perspective's name plus its prefix. */
const MAX_PILE_ID = 320;

/** Whatever was in the URL, as a pile id the shelf can look for. */
export function normalizePileId(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const id = value.trim().slice(0, MAX_PILE_ID);
  return id.startsWith("of:") || id.startsWith("as:") ? id : undefined;
}

function uniqueBy(games: GameSummary[], read: (game: GameSummary) => string | null): GameSummary[] {
  const seen = new Set<string | null>();
  return games.filter(game => {
    const key = read(game);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
